const DEFAULT_POLICY = Object.freeze({
  firstAfter: 4,
  interval: 10,
  maximum: 3,
});

export const HOME_LATEST_AD_PLACEMENT = Object.freeze({
  enabled: true,
  placementId: "home.latest.inline",
  formats: ["image"],
  ...DEFAULT_POLICY,
});

function normalizeRows(rows) {
  return Array.isArray(rows) ? rows.filter((row) => row?.id) : [];
}

function placementTargets({ firstAfter, interval, maximum }) {
  const targets = [];
  for (let sequence = 0; sequence < maximum; sequence += 1) {
    targets.push(firstAfter + sequence * interval);
  }
  return targets;
}

function placementPolicyKey(placement) {
  return [
    placement.placementId,
    placement.firstAfter,
    placement.interval,
    placement.maximum,
    ...(placement.formats || []),
  ].join(":");
}

export function reconcileFeedAdLedger({
  rows,
  previous = [],
  placement = HOME_LATEST_AD_PLACEMENT,
  sessionId,
} = {}) {
  if (!placement?.enabled || !placement.placementId || !sessionId) return [];

  const committedRows = normalizeRows(rows);
  const rowIds = new Set(committedRows.map((row) => row.id));
  const policyKey = placementPolicyKey(placement);
  const compatiblePrevious = previous.every((entry) => entry.policyKey === policyKey)
    ? previous
    : [];
  const bySequence = new Map(compatiblePrevious.map((entry) => [entry.sequence, entry]));
  const targets = placementTargets(placement);
  let changed = compatiblePrevious !== previous
    || (previous.length !== targets.length && previous.length !== 0);
  const next = [];

  for (let sequence = 0; sequence < targets.length; sequence += 1) {
    const existing = bySequence.get(sequence);
    if (existing) {
      const retired = existing.retired || !rowIds.has(existing.anchorId);
      const entry = retired === existing.retired ? existing : { ...existing, retired };
      if (entry !== existing) changed = true;
      next.push(entry);
      continue;
    }

    const target = targets[sequence];
    if (committedRows.length < target) continue;
    next.push({
      sequence,
      anchorId: committedRows[target - 1].id,
      opportunityId: `${sessionId}-${sequence + 1}`,
      placementId: placement.placementId,
      formats: [...placement.formats],
      policyKey,
      retired: false,
    });
    changed = true;
  }

  if (!changed && next.length === previous.length) return previous;
  return next;
}

export function activeOpportunityByAnchor(ledger = []) {
  return new Map(
    ledger
      .filter((entry) => entry && !entry.retired && entry.anchorId)
      .map((entry) => [entry.anchorId, entry]),
  );
}
